/* MyceliumSplash.tsx — Variant 02 · "Mycelium Network"
 * Mycelium threads grow outward from a central spore in organic curved paths,
 * tipped with pulsing nodes, colour emerald(center)→violet(tips). Honours
 * prefers-reduced-motion. Pure React + SVG + inline styles + one <style>.
 *
 * Usage:  {loading && <MyceliumSplash onDone={() => setLoading(false)} />}
 */
import { useEffect, useId, useState } from 'react';

interface MyceliumSplashProps {
  onDone: () => void;
  duration?: number; // default 1700
}

// Palette — VIVID (electric). For the muted brand palette swap to:
// { bg:'#0a0c11', ok:'#6ee7b7', okDeep:'#34d399', calm:'#c4b5fd', text:'#e8e6df', t4:'rgba(232,230,223,0.40)' }
const C = { bg: '#070809', ok: '#10f5a3', okDeep: '#00c97b', calm: '#b98cff', text: '#ffffff', t4: 'rgba(255,255,255,0.42)' };

function pickStatus(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 9)   return 'sabah_check.init';
  if (h >= 9 && h < 18)  return 'sensor_stream.connect';
  if (h >= 18 && h < 22) return 'akşam_triage.start';
  return 'nöbet_modu.aktif';
}

type Thread = { d: string; ex: number; ey: number; delay: number; branch: { d: string; ex: number; ey: number } | null };

function makeThreads(): Thread[] {
  const N = 9; const r = () => Math.random(); const out: Thread[] = [];
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2 + (r() - 0.5) * 0.4;
    const rad = 72 + r() * 17;
    const ex = 100 + Math.cos(ang) * rad, ey = 100 + Math.sin(ang) * rad;
    const perp = ang + Math.PI / 2, off = (r() - 0.5) * 36;
    const c1x = 100 + Math.cos(ang) * rad * 0.30 + Math.cos(perp) * off * 0.4;
    const c1y = 100 + Math.sin(ang) * rad * 0.30 + Math.sin(perp) * off * 0.4;
    const c2x = 100 + Math.cos(ang) * rad * 0.62 + Math.cos(perp) * off;
    const c2y = 100 + Math.sin(ang) * rad * 0.62 + Math.sin(perp) * off;
    const d = `M100 100 C ${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`;
    const delay = (i % 3) * 0.6 + Math.floor(i / 3) * 0.18 + r() * 0.18;
    let branch: Thread['branch'] = null;
    if (r() > 0.45) {
      const bAng = ang + (r() > 0.5 ? 1 : -1) * (0.4 + r() * 0.4);
      const sx = 100 + Math.cos(ang) * rad * 0.6, sy = 100 + Math.sin(ang) * rad * 0.6;
      const bl = 20 + r() * 16, bex = sx + Math.cos(bAng) * bl, bey = sy + Math.sin(bAng) * bl;
      const bcx = sx + Math.cos(bAng) * bl * 0.5 + (r() - 0.5) * 12, bcy = sy + Math.sin(bAng) * bl * 0.5 + (r() - 0.5) * 12;
      branch = { d: `M${sx.toFixed(1)} ${sy.toFixed(1)} Q ${bcx.toFixed(1)} ${bcy.toFixed(1)} ${bex.toFixed(1)} ${bey.toFixed(1)}`, ex: bex, ey: bey };
    }
    out.push({ d, ex, ey, delay, branch });
  }
  return out;
}

export default function MyceliumSplash({ onDone, duration = 1700 }: MyceliumSplashProps) {
  const uid = useId().replace(/:/g, '');
  const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const S = Math.max(0.55, duration / 1700);
  const [threads] = useState(makeThreads);
  const [status] = useState(pickStatus);
  const [exiting, setExiting] = useState(false);

  const growBase = 60 * S, growSpan = duration * 0.30, growDur = duration * 0.34, titleDelay = duration * 0.5;
  const delayMs = (t: Thread) => growBase + t.delay * (growSpan / 1.5);

  useEffect(() => {
    if (reduced) { const t = setTimeout(onDone, 800); return () => clearTimeout(t); }
    const T: ReturnType<typeof setTimeout>[] = [];
    T.push(setTimeout(() => setExiting(true), duration));
    T.push(setTimeout(onDone, duration + 500 * S));
    return () => T.forEach(clearTimeout);
  }, [duration]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="splash-screen" style={{
      position: 'fixed', inset: 0, zIndex: 100, overflow: 'hidden', background: C.bg,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      paddingBottom: 80,
      opacity: exiting ? 0 : 1, pointerEvents: exiting ? 'none' : 'all', transition: `opacity ${360 * S}ms ease`,
    }}>
      <div style={{
        position: 'relative', width: 240, height: 240,
        transform: exiting ? 'scale(0.92)' : 'scale(1)', opacity: exiting ? 0 : 1,
        transition: `transform ${380 * S}ms cubic-bezier(0.5,0,0.75,0), opacity ${320 * S}ms ease`,
      }}>
        <svg width={240} height={240} viewBox="0 0 200 200" style={{ overflow: 'visible' }}>
          <defs>
            <radialGradient id={`myc${uid}`} gradientUnits="userSpaceOnUse" cx="100" cy="100" r="92">
              <stop offset="0%" stopColor={C.ok} /><stop offset="55%" stopColor={C.ok} /><stop offset="100%" stopColor={C.calm} />
            </radialGradient>
          </defs>
          {!reduced && (
            <circle cx="100" cy="100" r="20" fill="none" stroke={C.ok} strokeWidth="0.8"
              style={{ animation: `myc_sense${uid} ${1100 * S}ms ease-out ${growBase}ms 1 both`, transformOrigin: 'center' }} />
          )}
          <g style={{ filter: reduced ? 'none' : `drop-shadow(0 0 3px ${C.ok})` }}>
            {threads.map((t, i) => (
              <g key={i}>
                <path d={t.d} fill="none" stroke={`url(#myc${uid})`} strokeWidth="1.9" strokeLinecap="round"
                  pathLength="1" strokeDasharray="1" strokeDashoffset={reduced ? 0 : 1}
                  style={{ animation: reduced ? 'none' : `myc_grow${uid} ${growDur}ms cubic-bezier(0.45,0,0.55,1) ${delayMs(t)}ms forwards` }} />
                {t.branch && (
                  <path d={t.branch.d} fill="none" stroke={`url(#myc${uid})`} strokeWidth="1.4" strokeLinecap="round"
                    pathLength="1" strokeDasharray="1" strokeDashoffset={reduced ? 0 : 1}
                    style={{ animation: reduced ? 'none' : `myc_grow${uid} ${growDur * 0.7}ms ease-out ${delayMs(t) + growDur * 0.55}ms forwards` }} />
                )}
                <circle cx={t.ex} cy={t.ey} r="2.7" fill={C.calm}
                  style={{ filter: `drop-shadow(0 0 5px ${C.calm})`, transformOrigin: `${t.ex}px ${t.ey}px`,
                    opacity: reduced ? 1 : 0, animation: reduced ? 'none' : `myc_node${uid} 1600ms ease-out ${delayMs(t) + growDur * 0.85}ms forwards` }} />
              </g>
            ))}
          </g>
        </svg>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          animation: reduced ? 'none' : `myc_spore${uid} 520ms cubic-bezier(0.16,1,0.3,1) both` }}>
          <span style={{ display: 'block', width: 15, height: 15, borderRadius: '50%',
            background: `radial-gradient(circle at 38% 32%, #fff, ${C.ok} 42%, ${C.okDeep} 100%)`,
            boxShadow: `0 0 10px ${C.ok}, 0 0 22px ${C.okDeep}88` }} />
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: '21%', left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        opacity: exiting ? 0 : 1, transition: `opacity ${300 * S}ms ease` }}>
        <div style={{ textAlign: 'center', lineHeight: 1.04, color: C.text, fontFamily: "'Fraunces', Georgia, serif",
          animation: reduced ? 'none' : `myc_title${uid} 560ms cubic-bezier(0.16,1,0.3,1) ${titleDelay}ms both` }}>
          <div style={{ fontWeight: 300, fontSize: 26, letterSpacing: '-0.02em' }}>Gurme</div>
          <div style={{ fontWeight: 400, fontStyle: 'italic', fontSize: 26, letterSpacing: '-0.02em', marginTop: -2 }}>Komuta</div>
        </div>
        <div style={{ marginTop: 12, fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 10, letterSpacing: '0.14em', color: C.t4,
          animation: reduced ? 'none' : `myc_sub${uid} 460ms ease ${titleDelay + 160}ms both` }}>{status}</div>
      </div>

      <style>{`
        @keyframes myc_grow${uid} { to { stroke-dashoffset: 0; } }
        @keyframes myc_node${uid} { 0% { opacity:0; transform:scale(0);} 30% { opacity:1; transform:scale(1.7);} 55% { opacity:1; transform:scale(1);} 100% { opacity:.85; transform:scale(1);} }
        @keyframes myc_sense${uid} { from { transform:scale(.3); opacity:.6;} to { transform:scale(4.6); opacity:0;} }
        @keyframes myc_spore${uid} { from { opacity:0; transform:translate(-50%,-50%) scale(.2);} to { opacity:1; transform:translate(-50%,-50%) scale(1);} }
        @keyframes myc_title${uid} { from { opacity:0; transform:translateY(8px);} to { opacity:1; transform:translateY(0);} }
        @keyframes myc_sub${uid} { from { opacity:0;} to { opacity:1;} }
      `}</style>
    </div>
  );
}
